import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Container, Row, Col, Button, OverlayTrigger, Popover } from 'react-bootstrap';
import { FaTruck } from 'react-icons/fa';
import { getAllRomaneios } from '../../services/Romaneio/romaneioService';
import RomaneiosFilter from '../Filters/RomaneiosFilter';
import CreateRomaneioModal from './CreateRomaneioModal';
import EditRomaneioModal from './EditRomaneioModal';
import { getCurrentDate, getCurrentDateMinusDays, formatDate } from '../Utils/utils';
import { ticketReportPdf } from '../../services/Reports/reportsService';
import { usePinVerification } from '../../context/PinVerificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePermissionsContext } from '../../context/PermissionsContext';

const Romaneios = () => {
  const { requirePinVerification } = usePinVerification();
  const { checkPermission, ToastComponent, hasPermission } = usePermissions();
  const { permissions } = usePermissionsContext();
  const [romaneios, setRomaneios] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(1000);
  const [showCreateRomaneioModal, setShowCreateRomaneioModal] = useState(false);
  const [showEditRomaneioModal, setShowEditRomaneioModal] = useState(false);
  const [editingRomaneioId, setEditingRomaneioId] = useState(null);
  const [activePopover, setActivePopover] = useState(null);
  const [openPopovers, setOpenPopovers] = useState({});
  const hidePopoverTimeoutsRef = useRef({});

  const HIDE_POPOVER_DELAY_MS = 350;

  useEffect(() => {
    return () => {
      Object.values(hidePopoverTimeoutsRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  const canView = useMemo(() => hasPermission('romaneioView'), [hasPermission, permissions]);
  const canCreate = useMemo(() => hasPermission('romaneioCreate'), [hasPermission, permissions]);
  const canUpdate = useMemo(() => hasPermission('romaneioUpdate'), [hasPermission, permissions]);
  const defaultFilters = {
    initialDate: getCurrentDateMinusDays(3),
    finalDate: getCurrentDate(),
    entry: true,
    exit: true,
    entryFinished: true,
    exitFinished: true,
    active: true,
  };

  const [filterParams, setFilterParams] = useState(defaultFilters);

  const fetchRomaneios = useCallback(
    async (filters = filterParams, currentPage = page, currentPageSize = pageSize) => {
      if (!canView) return;
      try {
        const data = await getAllRomaneios(filters, currentPage - 1, currentPageSize);
        setRomaneios(data.content);
      } catch (error) {
        console.error('Failed to fetch romaneios:', error);
      }
    },
    [filterParams, page, pageSize, canView]
  );

  const handleOpenCreateRomaneioModal = async () => {
    if (!checkPermission('romaneioCreate')) {
      return;
    }
    try {
      await requirePinVerification();
      setShowCreateRomaneioModal(true);
    } catch (error) {
      console.error('Erro ao verificar PIN:', error);
    }
  };

  const handleCloseCreateRomaneioModal = () => setShowCreateRomaneioModal(false);

  const handleOpenEditRomaneioModal = async (id) => {
    if (!checkPermission('romaneioUpdate')) {
      return;
    }
    try {
      await requirePinVerification();
    setEditingRomaneioId(id);
    setShowEditRomaneioModal(true);
    } catch (error) {
      console.error('Erro ao verificar PIN:', error);
    }
  };

  const handleCloseEditRomaneioModal = () => {
    setEditingRomaneioId(null);
    setShowEditRomaneioModal(false);
  };

  const handlePrintTicket = async (id) => {
    try {
      await ticketReportPdf(id);
    } catch (error) {
      console.error(`Erro ao imprimir o ticket para o romaneio ID: ${id}`, error);
    }
  };

  const groupedRomaneios = useCallback(() => {
    const entrada = romaneios.filter((r) => r.type === 'ENTRADA' && (r.balance === null || r.tare === null));
    const saida = romaneios.filter((r) => r.type === 'SAIDA' && (r.balance === null || r.tare === null));
    const entradaFinalizada = romaneios.filter((r) => r.type === 'ENTRADA' && r.balance !== null && r.tare !== null);
    const saidaFinalizada = romaneios.filter((r) => r.type === 'SAIDA' && r.balance !== null && r.tare !== null);

    return { entrada, saida, entradaFinalizada, saidaFinalizada };
  }, [romaneios]);

  useEffect(() => {
    const disableRightClick = (event) => {
      event.preventDefault();
    };
  
    document.addEventListener("contextmenu", disableRightClick);
  
    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);  

  const renderTrucks = (trucks, color) => {
    const scheduleClosePopover = (id) => {
      clearTimeout(hidePopoverTimeoutsRef.current[id]);
      hidePopoverTimeoutsRef.current[id] = setTimeout(() => {
        setOpenPopovers((prev) => {
          const next = { ...prev };
          delete next[id];
          return next;
        });
        delete hidePopoverTimeoutsRef.current[id];
      }, HIDE_POPOVER_DELAY_MS);
    };

    const cancelClosePopover = (id) => {
      clearTimeout(hidePopoverTimeoutsRef.current[id]);
      delete hidePopoverTimeoutsRef.current[id];
    };

    return trucks.map((truck) => {
      const formatUpdatedAt = (dateString) => {
        if (!dateString) return 'N/A';
        try {
          const date = new Date(dateString);
          return date.toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
        } catch (e) {
          return dateString;
        }
      };

      const clienteCompleto = truck.clienteNome
        ? (truck.clienteDocumento ? `${truck.clienteNome} - ${truck.clienteDocumento}` : truck.clienteNome)
        : 'N/A';

      const produtoLabel =
        truck.produtoDescricao ?? truck.product?.description ?? truck.product?.name ?? null;

      const isPopoverOpen = openPopovers[truck.id] || false;

      const handleMouseEnterTruck = () => {
        cancelClosePopover(truck.id);
        setOpenPopovers((prev) => ({ ...prev, [truck.id]: true }));
      };

      const handleMouseLeaveTruck = () => {
        scheduleClosePopover(truck.id);
      };

      const handleMouseEnterPopover = () => {
        cancelClosePopover(truck.id);
        setOpenPopovers((prev) => ({ ...prev, [truck.id]: true }));
      };

      const handleMouseLeavePopover = () => {
        scheduleClosePopover(truck.id);
      };

      const closePopover = () => {
        cancelClosePopover(truck.id);
        setOpenPopovers((prev) => {
          const newState = { ...prev };
          delete newState[truck.id];
          return newState;
        });
      };

      const popover = (
        <Popover 
          id={`popover-${truck.id}`} 
          className="custom-romaneio-popover"
          onMouseEnter={handleMouseEnterPopover}
          onMouseLeave={handleMouseLeavePopover}
        >
          <Popover.Body>
            <div style={{ textAlign: 'left', fontSize: '12px' }}>
              <div><strong>Placa:</strong> {truck.licensePlate || 'N/A'}</div>
              <div><strong>Motorista:</strong> {truck.motoristaNome || 'N/A'}</div>
              <div><strong>Produto:</strong> {produtoLabel || 'N/A'}</div>
              <div><strong>Cliente:</strong> {clienteCompleto}</div>
              <div style={{ whiteSpace: 'nowrap' }}><strong>Última atualização:</strong> {formatUpdatedAt(truck.updatedAt)}</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '10px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
            <Button
              variant="link"
              className="popover-button"
                style={{ padding: '5px', textAlign: 'left', fontSize: '12px', color: '#000' }}
              onClick={() => {
                handlePrintTicket(truck.id);
                closePopover();
              }}
            >
              Imprimir Romaneio
            </Button>
              {canUpdate && (
            <Button
              variant="link"
              className="popover-button"
                  style={{ padding: '5px', textAlign: 'left', fontSize: '12px', color: '#000' }}
              onClick={() => {
                handleOpenEditRomaneioModal(truck.id);
                closePopover();
              }}
            >
              Visualizar Romaneio
            </Button>
              )}
            </div>
          </Popover.Body>
        </Popover>
      );
  
      return (
        <OverlayTrigger
          key={truck.id}
          trigger="hover"
          placement="top"
          overlay={popover}
          show={isPopoverOpen}
          popperConfig={{
            modifiers: [
              { name: 'offset', options: { offset: [0, 6] } },
              { name: 'preventOverflow', options: { padding: 8 } },
            ],
          }}
          onToggle={(show) => {
            if (show) {
              cancelClosePopover(truck.id);
              setOpenPopovers((prev) => ({ ...prev, [truck.id]: true }));
            } else {
              scheduleClosePopover(truck.id);
            }
          }}
        >
          <div
            className="truck-item"
            style={{ cursor: 'pointer' }}
            onMouseEnter={handleMouseEnterTruck}
            onMouseLeave={handleMouseLeaveTruck}
            onContextMenu={(e) => {
              e.preventDefault();
              setActivePopover(truck.id);
            }}
          >
            <FaTruck size={40} style={{ color }} />
            <div className="truck-plate">{truck.licensePlate}</div>
          </div>
        </OverlayTrigger>
      );
    });
  };  

  useEffect(() => {
    if (canView) {
    fetchRomaneios();
    }
  }, [page, pageSize, fetchRomaneios, canView]);

  const handleFilter = (filters) => {
    if (!canView) return;
    setFilterParams(filters);
    setPage(1);
    fetchRomaneios(filters, 1, pageSize);
  };

  const handleClearFilter = () => {
    if (!canView) return;
    setPage(1);
    fetchRomaneios(defaultFilters, 1, pageSize);
  };

  const { entrada, saida, entradaFinalizada, saidaFinalizada } = groupedRomaneios();

  if (!canView) {
    return (
      <>
        <ToastComponent />
        <Container>
          <h3 className="mt-2 mb-3 text-center text-md-start">Romaneios</h3>
        </Container>
      </>
    );
  }

  return (
    <Container>
      <ToastComponent />
      <h3 className="mt-2 mb-3 text-center text-md-start">Romaneios</h3>
      <Row className="mb-3">
        <Col xs={12} md="auto" className="d-flex justify-content-md-end justify-content-center mt-2 mt-md-0">
          {canCreate && (
          <Button variant="primary" className="button-custom" onClick={handleOpenCreateRomaneioModal}>
            Novo Romaneio
          </Button>
          )}
        </Col>
      </Row>
      {canView && <RomaneiosFilter onFilter={handleFilter} onClear={handleClearFilter} className="mt-3" />}
      <Row xs={12}>
        <Col md={3}>
          <p className="title-romaneio">Entrada em Suspenso</p>
          <div className="truck-container">{renderTrucks(entrada, '#DAA520')}</div>
        </Col>
        <Col md={3}>
          <p className="title-romaneio">Saída em Suspenso</p>
          <div className="truck-container">{renderTrucks(saida, '#4169E1')}</div>
        </Col>
        <Col md={3}>
          <p className="title-romaneio">Entrada Finalizada</p>
          <div className="truck-container">{renderTrucks(entradaFinalizada, '#228B22')}</div>
        </Col>
        <Col md={3}>
          <p className="title-romaneio">Saída Finalizada</p>
          <div className="truck-container">{renderTrucks(saidaFinalizada, '#8B0000')}</div>
        </Col>
      </Row>
      <CreateRomaneioModal
        show={showCreateRomaneioModal}
        handleClose={handleCloseCreateRomaneioModal}
        refreshRomaneios={fetchRomaneios}
      />
      <EditRomaneioModal
        show={showEditRomaneioModal}
        handleClose={handleCloseEditRomaneioModal}
        romaneioId={editingRomaneioId}
        refreshRomaneios={fetchRomaneios}
      />
    </Container>
  );
};

export default Romaneios;